import React, { memo, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DialogContent from '@mui/material/DialogContent';
import { t, useDownload } from '@cyberint/common-utils';
import PdfViewer from './PdfViewer';
import CSVViewer from './CSVViewer';
import TXTViewer from './TXTViewer';
import Modal from '../Modal';
import Icons from '../Icons';
import * as S from './styled';
import { useArrowNavigation } from './useArrowNavigation';

const IMAGE = 'image';
const PDF = 'pdf';
const CSV = 'csv';
const PLAIN = 'plain';
const EMPTY = [];
const SIZE = { width: '450px', height: '250px' };

const defaultPreviewComponents = {
  [PDF]: ({ file }) => <PdfViewer file={file} />,
  [IMAGE]: ({ file, onLoad }) => <S.Image alt={file?.name || ''} src={file?.url} loading="lazy" onLoad={onLoad} />,
  [CSV]: ({ file }) => <CSVViewer file={file} />,
  [PLAIN]: ({ file }) => <TXTViewer file={file} />
};

const getFileType = mimetype => {
  if (!mimetype) return null;

  const mainType = mimetype.split('/')[0];
  const subType = mimetype.split('/')[1];

  if (mainType === 'image') return IMAGE;
  if (mimetype === 'application/pdf') return PDF;
  if (subType === 'csv' || mimetype === 'text/csv') return CSV;
  if (mainType === 'text' || subType === 'plain') return PLAIN;

  return null;
};

const Preview = ({ files = EMPTY, selectedIndex = 0, opened = false, onClose, onDownload, testId }) => {
  const [selectedIdx, setSelectedIdx] = useState(selectedIndex);
  const [isDangerous, setDangerous] = useState(false);
  const { download, isDownloading } = useDownload();

  const setActiveIndex = idx => {
    setSelectedIdx(idx);
    if (files?.[idx]?.is_safe === false) {
      setDangerous(true);
    }
  };

  useEffect(() => {
    if (opened) {
      setActiveIndex(selectedIndex);
    }
  }, [selectedIndex, opened]);

  const currentFile = files?.[selectedIdx];

  const previewComponent = useMemo(() => {
    if (!currentFile) return null;

    if (currentFile.is_safe === false) return <div>{t('common.no_preview_dangerous_file')}</div>;

    const fileType = getFileType(currentFile.mimetype);
    const PreviewComponent = defaultPreviewComponents[fileType];

    if (!PreviewComponent) return <div>{t('common.preview_not_available_for_file_type')}</div>;

    return <PreviewComponent file={currentFile} />;
  }, [currentFile]);

  const filesLength = files?.length ?? 0;
  const prevEnabled = selectedIdx > 0;
  const nextEnabled = selectedIdx + 1 < filesLength;

  const stopPropagation = e => e.stopPropagation();

  const handlePrevFile = () => {
    if (selectedIdx > 0) {
      setActiveIndex(selectedIdx - 1);
    }
  };

  const handleNextFile = () => {
    if (selectedIdx < filesLength - 1) {
      setActiveIndex(selectedIdx + 1);
    }
  };

  useArrowNavigation({
    enabled: opened,
    onLeft: handlePrevFile,
    onRight: handleNextFile,
    leftEnabled: prevEnabled,
    rightEnabled: nextEnabled
  });

  const isSingleDangerousFile = filesLength === 1 && files[0]?.is_safe === false;

  const onCloseDangerousModal = () => {
    setDangerous(false);
    /* In case of single file mode, modal will be opened immediately, so we need to trigger onClose */
    isSingleDangerousFile && onClose?.();
  };

  const downloadFile = () => {
    download({ url: currentFile.url, fileName: currentFile.name });
    onCloseDangerousModal();
  };

  const handleFileDownload = () => {
    if (!currentFile) return;
    if (typeof onDownload === 'function') {
      onDownload(currentFile.name, currentFile.url);
    } else if (currentFile.is_safe === false) {
      setDangerous(true);
    } else {
      downloadFile();
    }
  };

  if (!opened) return null;

  const hasMultipleFiles = filesLength > 1;

  return (
    <>
      {!isSingleDangerousFile && (
        <S.PreviewDialog open={opened} data-testid={testId} onClick={stopPropagation} onClose={onClose}>
          <S.Header>
            <S.AttachmentInfo>
              <Icons.Image />
              <span>{currentFile?.name}</span>
            </S.AttachmentInfo>

            {hasMultipleFiles && (
              <S.FileCount>
                <span>{`${selectedIdx + 1} of ${filesLength}`}</span>
              </S.FileCount>
            )}

            <S.Actions className="dark-mode">
              <S.IconWrapper>
                <S.DownloadIcon
                  onClick={handleFileDownload}
                  testId={testId ? `button-download-${testId}` : 'download-button'}
                />
                {isDownloading && <S.LoadingIcon />}
              </S.IconWrapper>
              <S.CloseIcon onClick={onClose} />
            </S.Actions>
          </S.Header>

          {hasMultipleFiles && (
            <>
              <S.ArrowLeft $enabled={prevEnabled} onClick={handlePrevFile} data-testid="prev-file-btn" />
              <S.ArrowRight $enabled={nextEnabled} onClick={handleNextFile} data-testid="next-file-btn" />
            </>
          )}

          <DialogContent>
            <S.PreviewWrapper>{previewComponent}</S.PreviewWrapper>
          </DialogContent>
        </S.PreviewDialog>
      )}
      <Modal
        title={t('common.download_dangerous_file_title')}
        content={t('common.download_dangerous_file_msg')}
        onOk={downloadFile}
        size={SIZE}
        okText={t('buttons.download')}
        testId="modal-download-dangerous"
        okTestId="button-download-dangerous"
        cancelText={t('buttons.cancel')}
        onClose={onCloseDangerousModal}
        isOpen={isDangerous}
      />
    </>
  );
};

Preview.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      mimetype: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ),
  selectedIndex: PropTypes.number,
  opened: PropTypes.bool,
  onClose: PropTypes.func,
  onDownload: PropTypes.func,
  testId: PropTypes.string
};

export default memo(Preview);
